import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { QuestionRendererProps } from './types';
import { FALLBACK_COLOR, hexToRgba } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
import { hapticManager } from '../../../utils/hapticManager';
import { ChevronLeft, ChevronRight } from 'lucide-react';
/**
 * DoubleNumberLineRenderer — Interactive dubbele getallenlijn.
 *
 * The student sees two parallel horizontal number lines that share the same
 * coordinate space (left edge = 0% on both, right edge = max on both). Known
 * anchor points are pinned on BOTH lines and visually connected by a dashed
 * vertical line — this is the schoolbook "Wereld in Getallen" model for
 * proportional reasoning across procenten and verhoudingen.
 *
 * The student drags a marker along the "ask" line; the position is projected
 * to the OTHER line via a dashed projection so that the proportional link is
 * always visible. Answer payload is the numeric value of the marker on the
 * asked scale.
 *
 * Didactic anchor:
 *  - WiG / Pluspunt: dubbele getallenlijn voor procenten van een geheel
 *  - Tier-band: 7–14 (group 6 plus tot eind group 8)
 *  - DOK: 2 (strategische toepassing van verhoudings-redeneren)
 *
 * Validator: numeric tolerance, default 5% of askFor scale range.
 */
export const DoubleNumberLineRenderer: React.FC<QuestionRendererProps> = ({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}) => {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data?.doubleNumberLine;
  const hasConfig = !!config && !!config.topScale && !!config.bottomScale && Array.isArray(config.knownPoints) && (config.askFor === 'top' || config.askFor === 'bottom') && typeof config.correctValue === 'number';
  // ── Hooks must be unconditional ──────────────────────────────────────────
  const svgRef = useRef<SVGSVGElement>(null);
  const [position, setPosition] = useState<number | null>(null); // 0..1 along the shared axis
  const [dragging, setDragging] = useState(false);
  // Layout constants (viewBox space)
  const VB_WIDTH = 600;
  const VB_HEIGHT = 220;
  const PADDING_X = 50;
  const TOP_Y = 70;
  const BOTTOM_Y = 160;
  const AXIS_LEFT = PADDING_X;
  const AXIS_RIGHT = VB_WIDTH - PADDING_X;
  const AXIS_LENGTH = AXIS_RIGHT - AXIS_LEFT;
  // Reset on question change
  useEffect(() => {
    setPosition(null);
    setDragging(false);
  }, [question.id]);
  // Ready state
  useEffect(() => {
    onReadyChange?.(position !== null);
  }, [position, onReadyChange]);
  // ── Derived scales ───────────────────────────────────────────────────────
  const topScale = config?.topScale;
  const bottomScale = config?.bottomScale;
  const askFor = config?.askFor ?? 'bottom';
  const askedScale = askFor === 'top' ? topScale : bottomScale;
  const otherScale = askFor === 'top' ? bottomScale : topScale;
  const valueAtFraction = useCallback((frac: number, scale: {
    min: number;
    max: number;
  } | undefined) => {
    if (!scale) return 0;
    return scale.min + frac * (scale.max - scale.min);
  }, []);
  const fractionAtValue = useCallback((value: number, scale: {
    min: number;
    max: number;
  } | undefined) => {
    if (!scale || scale.max === scale.min) return 0;
    const f = (value - scale.min) / (scale.max - scale.min);
    return Math.max(0, Math.min(1, f));
  }, []);
  const currentValue = useMemo(() => {
    if (position === null || !askedScale) return null;
    return valueAtFraction(position, askedScale);
  }, [position, askedScale, valueAtFraction]);
  // NOTE: `currentOtherValue` was previously rendered as live text inside the SVG
  // and leaked the answer. Removed — only the visual projection marker remains.
  // ── Drag handling ────────────────────────────────────────────────────────
  const fractionFromClientX = useCallback((clientX: number): number => {
    const svg = svgRef.current;
    if (!svg) return 0;
    const rect = svg.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    const vbX = ratio * VB_WIDTH;
    const f = (vbX - AXIS_LEFT) / AXIS_LENGTH;
    return Math.max(0, Math.min(1, f));
  }, []);
  const handlePointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    hapticManager.hapticLight();
    setPosition(fractionFromClientX(e.clientX));
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    setPosition(fractionFromClientX(e.clientX));
  };
  const handlePointerUp = (e: React.PointerEvent) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (dragging) {
      hapticManager.hapticMedium();
    }
    setDragging(false);
  };
  const handleTrackClick = (e: React.MouseEvent) => {
    hapticManager.hapticMedium();
    setPosition(fractionFromClientX(e.clientX));
  };
  const handleNudge = (direction: -1 | 1) => {
    if (shouldValidate) return;
    hapticManager.hapticLight();
    setPosition(prev => {
      const current = prev ?? 0.5;
      // Nudge by 1% of the scale
      const next = Math.max(0, Math.min(1, current + direction * 0.01));
      return next;
    });
  };
  // ── Edge-triggered submit ────────────────────────────────────────────────
  const answerPayload = useMemo(() => {
    if (currentValue === null) return 0;
    return Number(currentValue.toFixed(4));
  }, [currentValue]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  // ── Early bail for malformed config (after all hooks) ────────────────────
  if (!hasConfig || !topScale || !bottomScale || !askedScale || !otherScale) {
    return <div className="text-center p-8 bg-amber-50 rounded-xl border-2 border-amber-200" data-id="element-11485" data-mp="3sk1ae2240ram">
        <p className="text-amber-700 font-bold" data-id="element-11486" data-mp="4f369tmmx9c2">
          ⚠️ Configuratie ontbreekt (doubleNumberLine)
        </p>
        <p className="text-sm text-amber-600 mt-2" data-id="element-11487" data-mp="8fn5d2qrqn0u">
          Deze vraag mist <code data-id="element-11488" data-mp="dfhup1uwb4js">data.doubleNumberLine</code> met
          <code data-id="element-11489" data-mp="dgsyh1occcaw"> topScale</code>, <code data-id="element-11490" data-mp="dgaxxsunbbx6">bottomScale</code>,{' '}
          <code data-id="element-11491" data-mp="if4a9hy74wvr">knownPoints</code>, <code data-id="element-11492" data-mp="idjg3tfsnfyf">askFor</code> en{' '}
          <code data-id="element-11493" data-mp="ici1xg3hedch">correctValue</code>.
        </p>
      </div>;
  }
  // ── Format helpers ───────────────────────────────────────────────────────
  const formatValue = (v: number, scale: typeof topScale): string => {
    const abs = Math.abs(v);
    const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
    const rounded = Number(v.toFixed(decimals));
    const num = rounded.toLocaleString('nl-NL');
    return scale?.unit ? `${num}${scale.unit}` : num;
  };
  // Tick generation: ~5 ticks across the scale
  const TICK_COUNT = 5;
  const ticks = Array.from({
    length: TICK_COUNT + 1
  }, (_, i) => i / TICK_COUNT);
  const promptLabel = config!.promptLabel ?? (askFor === 'top' ? `Welk ${topScale.label ?? 'getal'} hoort hierbij?` : `Welk ${bottomScale.label ?? 'getal'} hoort hierbij?`);
  const markerX = position !== null ? AXIS_LEFT + position * AXIS_LENGTH : null;
  const isReady = position !== null;
  return <div className="w-full max-w-2xl mx-auto space-y-5" data-id="element-11494" data-mp="2gotahbu1ge1v">
      {/* SVG — the double number line */}
      <div className="bg-white rounded-2xl p-5 sm:p-6 border-2 border-slate-200 shadow-sm" data-id="element-11495" data-mp="2cmoiehj8nphc">
        <svg ref={svgRef} viewBox={`0 0 ${VB_WIDTH} ${VB_HEIGHT}`} className="w-full h-auto select-none touch-none" style={{
        maxHeight: 280
      }} role="img" aria-label={`Dubbele getallenlijn. ${topScale.label ?? 'Bovenste schaal'} van ${formatValue(topScale.min, topScale)} tot ${formatValue(topScale.max, topScale)}. ${bottomScale.label ?? 'Onderste schaal'} van ${formatValue(bottomScale.min, bottomScale)} tot ${formatValue(bottomScale.max, bottomScale)}.`} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp} data-id="element-11496" data-mp="27vgaxr1gye6t">
          {/* Scale labels (left side) */}
          {topScale.label && <text x={AXIS_LEFT - 8} y={TOP_Y + 4} textAnchor="end" fontSize="12" fontWeight="700" fill="#64748b" data-id="element-11497" data-mp="37tnvcmyom5cn">
              {topScale.label}
            </text>}
          {bottomScale.label && <text x={AXIS_LEFT - 8} y={BOTTOM_Y + 4} textAnchor="end" fontSize="12" fontWeight="700" fill="#64748b" data-id="element-11498" data-mp="2k5xkuiwm57qk">
              {bottomScale.label}
            </text>}

          {/* Top axis */}
          <line x1={AXIS_LEFT} y1={TOP_Y} x2={AXIS_RIGHT} y2={TOP_Y} stroke={askFor === 'top' ? color : '#94a3b8'} strokeWidth={askFor === 'top' ? 3 : 2} strokeLinecap="round" data-id="element-11499" data-mp="2f4qd2juh08jw" />
          {/* Bottom axis */}
          <line x1={AXIS_LEFT} y1={BOTTOM_Y} x2={AXIS_RIGHT} y2={BOTTOM_Y} stroke={askFor === 'bottom' ? color : '#94a3b8'} strokeWidth={askFor === 'bottom' ? 3 : 2} strokeLinecap="round" data-id="element-11500" data-mp="25iyvqul8apf0" />

          {/* Ticks + tick labels (endpoints + midpoint).
            Midpoint label is rendered as a faint reference so the student
            still has a proportional anchor without seeing live drag values. */}
          {ticks.map((t, i) => {
          const x = AXIS_LEFT + t * AXIS_LENGTH;
          const isEndpoint = i === 0 || i === TICK_COUNT;
          const isMidpoint = i === Math.floor(TICK_COUNT / 2);
          const topVal = valueAtFraction(t, topScale);
          const botVal = valueAtFraction(t, bottomScale);
          const showLabel = isEndpoint || isMidpoint;
          const labelOpacity = isEndpoint ? 1 : 0.55;
          return <g key={i} data-id="element-11501" data-mp="y94g3gde90mt" data-mpi={i}>
                <line x1={x} y1={TOP_Y - 6} x2={x} y2={TOP_Y + 6} stroke="#cbd5e1" strokeWidth={1.5} data-id="element-11502" data-mp="y8bh51kofff8" />
                <line x1={x} y1={BOTTOM_Y - 6} x2={x} y2={BOTTOM_Y + 6} stroke="#cbd5e1" strokeWidth={1.5} data-id="element-11503" data-mp="1pzr08ln0yk0p" />
                {showLabel && <>
                    <text x={x} y={TOP_Y - 14} textAnchor="middle" fontSize="11" fontWeight={isEndpoint ? 600 : 500} fill="#64748b" opacity={labelOpacity} data-id="element-11504" data-mp="1bzpxt1r434j4">
                      {formatValue(topVal, topScale)}
                    </text>
                    <text x={x} y={BOTTOM_Y + 22} textAnchor="middle" fontSize="11" fontWeight={isEndpoint ? 600 : 500} fill="#64748b" opacity={labelOpacity} data-id="element-11505" data-mp="3rmehgcclpdpv">
                      {formatValue(botVal, bottomScale)}
                    </text>
                  </>}
              </g>;
        })}

          {/* Known anchor points — dot on each line + dashed connector */}
          {config!.knownPoints.map((kp, idx) => {
          const fTop = fractionAtValue(kp.top, topScale);
          const fBot = fractionAtValue(kp.bottom, bottomScale);
          // Use the average fraction for positioning (they should align)
          const f = (fTop + fBot) / 2;
          const x = AXIS_LEFT + f * AXIS_LENGTH;
          return <g key={`kp-${idx}`} data-id="element-11506" data-mp="2cwry8zv5bjpr" data-mpi={idx}>
                <line x1={x} y1={TOP_Y} x2={x} y2={BOTTOM_Y} stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="3 3" data-id="element-11507" data-mp="2cxtls3tclyod" />
                <circle cx={x} cy={TOP_Y} r={6} fill="#475569" data-id="element-11508" data-mp="30uphns6ogodd" />
                <circle cx={x} cy={BOTTOM_Y} r={6} fill="#475569" data-id="element-11509" data-mp="30swambs5k24u" />
                <text x={x} y={TOP_Y - 14} textAnchor="middle" fontSize="11" fontWeight="700" fill="#1e293b" data-id="element-11510" data-mp="2vul0dzxy3pm3">
                  {formatValue(kp.top, topScale)}
                </text>
                <text x={x} y={BOTTOM_Y + 22} textAnchor="middle" fontSize="11" fontWeight="700" fill="#1e293b" data-id="element-11511" data-mp="3jm06m3suer2m">
                  {formatValue(kp.bottom, bottomScale)}
                </text>
              </g>;
        })}

          {/* Invisible hit-area track for clicking anywhere on the band */}
          <rect x={AXIS_LEFT} y={TOP_Y - 20} width={AXIS_LENGTH} height={BOTTOM_Y - TOP_Y + 40} fill="transparent" onClick={handleTrackClick} style={{
          cursor: 'pointer'
        }} data-id="element-11512" data-mp="3p9bsd0rxttih" />

          {/* Active marker + projection */}
          {markerX !== null && <g data-id="element-11513" data-mp="36iaxooxuok4q">
              {/* Projection line */}
              <line x1={markerX} y1={TOP_Y} x2={markerX} y2={BOTTOM_Y} stroke={color} strokeWidth={2} strokeDasharray="4 3" opacity={0.6} data-id="element-11514" data-mp="3ahtgnf642l4x" />

              {/* Projected (passive) marker on the OTHER line */}
              <motion.circle cx={markerX} cy={askFor === 'top' ? BOTTOM_Y : TOP_Y} r={7} fill="white" stroke={color} strokeWidth={2.5} animate={{
            scale: dragging ? 1.05 : 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} data-id="element-11515" data-mp="7ovmacdxjt3p" />

              {/* Active (draggable) marker on the ASK line */}
              <motion.circle cx={markerX} cy={askFor === 'top' ? TOP_Y : BOTTOM_Y} r={16} fill={color} stroke="white" strokeWidth={3} animate={{
            scale: dragging ? 1.15 : 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 18
          }} style={{
            filter: `drop-shadow(0 4px 8px ${hexToRgba(color, 0.4)})`,
            cursor: dragging ? 'grabbing' : 'grab'
          }} data-id="element-11516" data-mp="3c3db8g5tu99x" />

              {/* NOTE: live numeric value labels removed — they leaked the answer.
               The student now sees the marker position without the exact number,
               which forces them to reason about the proportional position
               relative to known anchors (the schoolbook intent of the model). */}
            </g>}
        </svg>
      </div>

      {/* Prompt / status readout — does NOT show the chosen numeric value
              (that would leak the answer). Shows a neutral progress indicator
              and prompts the student to drag / nudge. The submit payload still
              carries the numeric position to the validator. */}
      <div className="rounded-2xl p-4 sm:p-5 border-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4" style={{
      backgroundColor: hexToRgba(color, 0.07),
      borderColor: hexToRgba(color, 0.25)
    }} data-id="element-11517" data-mp="2jbfmm6ctxxgy">
        <div className="flex-1" data-id="element-11518" data-mp="t9gmbtcq0k8f">
          <p className="text-sm font-bold uppercase tracking-wide" style={{
          color
        }} data-id="element-11519" data-mp="t8f1ix5wkjdi">
            {promptLabel}
          </p>
          <p className="text-xs text-slate-500 mt-1" data-id="element-11520" data-mp="l2phgxdbtyg">
            {isReady ? 'Verschuif tot het klopt, druk dan op Check.' : `Sleep de stip op de ${askFor === 'top' ? 'bovenste' : 'onderste'} lijn of gebruik de knoppen.`}
          </p>
        </div>
        <div className="flex items-center gap-3 self-center sm:self-auto" data-id="element-11521" data-mp="5j48k1usnt9x">
          <button onClick={() => handleNudge(-1)} disabled={shouldValidate || position === 0} className="w-12 h-12 rounded-xl bg-white border-2 flex items-center justify-center text-slate-600 hover:bg-slate-50 active:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" style={{
          borderColor: hexToRgba(color, 0.25)
        }} aria-label="Schuif naar links" data-id="element-11522" data-mp="3dz61y8l90a26">
            <ChevronLeft className="w-6 h-6" data-id="element-11523" data-mp="1ff9eelf74jb0" />
          </button>
          {/* Neutral "chosen" indicator — a colored dot when ready, hollow when not.
           Intentionally does NOT show the numeric value to avoid leaking the answer. */}
          <div className="px-4 py-2 rounded-xl bg-white border-2 min-w-[110px] text-center h-12 flex items-center justify-center gap-2" style={{
          borderColor: hexToRgba(color, 0.25)
        }} aria-label={isReady ? 'Positie gekozen' : 'Nog geen keuze'} data-id="element-11524" data-mp="2c84u8ut33iup">
            <span className="rounded-full" style={{
            width: 12,
            height: 12,
            backgroundColor: isReady ? color : 'transparent',
            border: isReady ? 'none' : `2px solid ${hexToRgba(color, 0.4)}`
          }} data-id="element-11525" data-mp="mrn8h6ihx2kz" />
            <span className="text-sm font-bold" style={{
            color: isReady ? color : '#94a3b8'
          }} data-id="element-11526" data-mp="1eeonz43zbezn">
              {isReady ? 'gekozen' : 'kies een plek'}
            </span>
          </div>
          <button onClick={() => handleNudge(1)} disabled={shouldValidate || position === 1} className="w-12 h-12 rounded-xl bg-white border-2 flex items-center justify-center text-slate-600 hover:bg-slate-50 active:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" style={{
          borderColor: hexToRgba(color, 0.25)
        }} aria-label="Schuif naar rechts" data-id="element-11527" data-mp="225kkgejdqyd8">
            <ChevronRight className="w-6 h-6" data-id="element-11528" data-mp="2ky6h31242syv" />
          </button>
        </div>
      </div>
    </div>;
};